import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { ClaimObjectionStatus, Page, RollClaim, RollClaimType, RollObjection, RollRevision } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, Tabs } from '../../ui/components';

const OFFICER_ROLES = ['REGISTRATION_OFFICER', 'ELECTORAL_COMMISSIONER'];
const CLAIM_TYPES: RollClaimType[] = ['NEW_REGISTRATION', 'ADDRESS_CHANGE', 'REINSTATEMENT'];

const TRANSITIONS: Record<string, { action: string; label: string }> = {
  SCHEDULED: { action: 'open-claims', label: 'Open claims window' },
  CLAIMS_OPEN: { action: 'open-objections', label: 'Open objections window' },
  OBJECTIONS_OPEN: { action: 'send-for-review', label: 'Send for registrar review' },
  REGISTRAR_REVIEW: { action: 'certify', label: 'Certify revision' },
  CERTIFIED: { action: 'close', label: 'Close revision' },
};

export function RollRevisionDetail() {
  const { id } = useParams<{ id: string }>();
  const { persona } = useAuth();
  const isOfficer = persona?.roles.some((r) => OFFICER_ROLES.includes(r)) ?? false;
  const [tab, setTab] = useState<'claims' | 'objections'>('claims');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const revisionQ = useApiQuery<RollRevision>(id ? `/api/v1/m39/roll-revisions/${id}` : null);

  if (revisionQ.loading) return <Spinner label="Loading revision…" />;
  if (revisionQ.error) return <ErrorNote message={revisionQ.error} />;
  const r = revisionQ.data;
  if (!r) return null;

  const transition = TRANSITIONS[r.state];

  const doTransition = async () => {
    if (!transition) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.post(`/api/v1/m39/roll-revisions/${r.id}/${transition.action}`);
      revisionQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Transition failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title={r.title} subtitle={`Roll revision cycle · ${r.year}`} actions={<StatusBadge status={r.state} />} />

      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      {isOfficer && transition ? (
        <div className="card mb-6 flex items-center gap-3 p-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Lifecycle</span>
          <button className="btn-primary" onClick={doTransition} disabled={busy}>
            {busy ? 'Working…' : transition.label}
          </button>
        </div>
      ) : null}

      <Tabs tabs={[{ key: 'claims', label: 'Claims' }, { key: 'objections', label: 'Objections' }]} value={tab} onChange={setTab} />
      {tab === 'claims' ? <ClaimsPanel revision={r} isOfficer={isOfficer} /> : <ObjectionsPanel revision={r} isOfficer={isOfficer} />}
    </div>
  );
}

function ClaimsPanel({ revision, isOfficer }: { revision: RollRevision; isOfficer: boolean }) {
  const [showFile, setShowFile] = useState(false);
  const [decide, setDecide] = useState<RollClaim | null>(null);
  const listQ = useApiQuery<Page<RollClaim>>(isOfficer ? `/api/v1/m39/roll-revisions/${revision.id}/claims?limit=50` : null);

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <button className="btn-primary" onClick={() => setShowFile(true)} disabled={revision.state !== 'CLAIMS_OPEN'}>
          + File a claim
        </button>
      </div>
      {revision.state !== 'CLAIMS_OPEN' ? (
        <p className="mb-3 text-xs text-slate-400">Claims can only be filed while this revision is in the CLAIMS_OPEN state.</p>
      ) : null}

      {!isOfficer ? (
        <RoleNotice roles={OFFICER_ROLES} />
      ) : listQ.error ? (
        <ErrorNote message={listQ.error} />
      ) : listQ.loading ? (
        <Spinner />
      ) : (listQ.data?.items.length ?? 0) === 0 ? (
        <EmptyState title="No claims filed yet" />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Type</th>
                <th className="table-head">NIN</th>
                <th className="table-head">Status</th>
                <th className="table-head">Decided</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((c) => (
                <tr key={c.id}>
                  <td className="table-cell text-xs">{c.type.replaceAll('_', ' ')}</td>
                  <td className="table-cell font-mono text-xs">{c.nin}</td>
                  <td className="table-cell">
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="table-cell text-xs">{c.decisionReason ?? '—'}</td>
                  <td className="table-cell text-right">
                    {(['SUBMITTED', 'UNDER_REVIEW'] as ClaimObjectionStatus[]).includes(c.status) ? (
                      <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setDecide(c)}>
                        Decide
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showFile ? (
        <FileClaimModal
          revisionId={revision.id}
          onClose={() => setShowFile(false)}
          onDone={() => {
            setShowFile(false);
            listQ.reload();
          }}
        />
      ) : null}
      {decide ? (
        <DecideModal
          title={`Decide claim — ${decide.type.replaceAll('_', ' ')} (${decide.nin})`}
          onClose={() => setDecide(null)}
          onSubmit={async (status, reason) => {
            await api.post(`/api/v1/m39/roll/claims/${decide.id}/decide`, { status, reason });
            setDecide(null);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function FileClaimModal({ revisionId, onClose, onDone }: { revisionId: string; onClose: () => void; onDone: () => void }) {
  const [type, setType] = useState<RollClaimType>('NEW_REGISTRATION');
  const [nin, setNin] = useState('');
  const [detailsNote, setDetailsNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post<RollClaim>(`/api/v1/m39/roll-revisions/${revisionId}/claims`, {
        type,
        nin: nin || undefined,
        details: detailsNote ? { note: detailsNote } : {},
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to file claim');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="File a roll claim" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Claim type">
          <select className="field-input" value={type} onChange={(e) => setType(e.target.value as RollClaimType)}>
            {CLAIM_TYPES.map((t) => (
              <option key={t} value={t}>
                {t.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="NIN (leave blank to file for your own verified NIN)">
          <input className="field-input" value={nin} onChange={(e) => setNin(e.target.value)} placeholder="Officer-assisted only — otherwise uses your own NIN" />
        </Field>
        <Field label="Supporting note (optional)">
          <input className="field-input" value={detailsNote} onChange={(e) => setDetailsNote(e.target.value)} placeholder="Any context for the officer reviewing this" />
        </Field>
        <p className="text-xs text-slate-500">
          NEW_REGISTRATION and ADDRESS_CHANGE auto-adjudicate instantly against the M01 civil register. REINSTATEMENT always needs officer review.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Filing…' : 'File claim'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ObjectionsPanel({ revision, isOfficer }: { revision: RollRevision; isOfficer: boolean }) {
  const [showFile, setShowFile] = useState(false);
  const [decide, setDecide] = useState<RollObjection | null>(null);
  const listQ = useApiQuery<Page<RollObjection>>(isOfficer ? `/api/v1/m39/roll-revisions/${revision.id}/objections?limit=50` : null);

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <button className="btn-primary" onClick={() => setShowFile(true)} disabled={revision.state !== 'OBJECTIONS_OPEN'}>
          + File an objection
        </button>
      </div>
      {revision.state !== 'OBJECTIONS_OPEN' ? (
        <p className="mb-3 text-xs text-slate-400">Objections can only be filed while this revision is in the OBJECTIONS_OPEN state.</p>
      ) : null}

      {!isOfficer ? (
        <RoleNotice roles={OFFICER_ROLES} />
      ) : listQ.error ? (
        <ErrorNote message={listQ.error} />
      ) : listQ.loading ? (
        <Spinner />
      ) : (listQ.data?.items.length ?? 0) === 0 ? (
        <EmptyState title="No objections filed yet" />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Target NIN</th>
                <th className="table-head">Reason</th>
                <th className="table-head">Status</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((o) => (
                <tr key={o.id}>
                  <td className="table-cell font-mono text-xs">{o.targetNin}</td>
                  <td className="table-cell max-w-xs truncate text-xs text-slate-600">{o.reason}</td>
                  <td className="table-cell">
                    <StatusBadge status={o.status} />
                  </td>
                  <td className="table-cell text-right">
                    {(['SUBMITTED', 'UNDER_REVIEW'] as ClaimObjectionStatus[]).includes(o.status) ? (
                      <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setDecide(o)}>
                        Decide
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showFile ? (
        <FileObjectionModal
          revisionId={revision.id}
          onClose={() => setShowFile(false)}
          onDone={() => {
            setShowFile(false);
            listQ.reload();
          }}
        />
      ) : null}
      {decide ? (
        <DecideModal
          title={`Decide objection — target ${decide.targetNin}`}
          danger
          onClose={() => setDecide(null)}
          onSubmit={async (status, reason) => {
            await api.post(`/api/v1/m39/roll/objections/${decide.id}/decide`, { status, reason });
            setDecide(null);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function FileObjectionModal({ revisionId, onClose, onDone }: { revisionId: string; onClose: () => void; onDone: () => void }) {
  const [targetNin, setTargetNin] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m39/roll-revisions/${revisionId}/objections`, { targetNin, reason, evidenceRefs: [] });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to file objection');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="File an objection against a named elector" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Target elector's NIN">
          <input className="field-input" value={targetNin} onChange={(e) => setTargetNin(e.target.value)} />
        </Field>
        <Field label="Reason">
          <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. No longer resident in this district" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || targetNin.trim().length < 5 || reason.trim().length < 3}>
            {busy ? 'Filing…' : 'File objection'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function DecideModal({
  title,
  danger,
  onClose,
  onSubmit,
}: {
  title: string;
  danger?: boolean;
  onClose: () => void;
  onSubmit: (status: 'UPHELD' | 'REJECTED', reason: string) => Promise<void>;
}) {
  const [status, setStatus] = useState<'UPHELD' | 'REJECTED'>('UPHELD');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSubmit(status, reason);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Decision failed');
      setBusy(false);
    }
  };

  return (
    <Modal title={title} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Decision">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value as 'UPHELD' | 'REJECTED')}>
            <option value="UPHELD">Uphold</option>
            <option value="REJECTED">Reject</option>
          </select>
        </Field>
        <Field label="Reason">
          <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reasoning for the record" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className={danger ? 'btn-danger' : 'btn-primary'} onClick={submit} disabled={busy || reason.trim().length < 3}>
            {busy ? 'Deciding…' : 'Confirm decision'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
